import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Checkbox,
    CircularProgress,
    LinearProgress,
    MenuItem,
    Select,
    Typography,
    FormControl,
    InputLabel,
} from '@mui/material';
import api, { specialUserApi } from 'lib/api';
import { resolveKennitala } from './MembershipPhotoUpload';
import styles from './MembershipPhotoBulkUpload.module.scss';

function parseKennitalaFromFilename(filename) {
    const base = String(filename || '')
        .replace(/\.[^.]+$/, '')
        .trim();
    const digits = base.replace(/[\s-]+/g, '').replace(/\D+/g, '');
    if (digits.length !== 10) return null;
    // Accept only if original (sans extension) looks like kt forms after removing spaces/dashes
    const normalized = base.replace(/[\s-]+/g, '');
    if (!/^\d{10}$/.test(normalized)) return null;
    return digits;
}

function fileToDataUrl(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

export default function MembershipPhotoBulkUpload({
    open,
    onClose,
    companies,
    members,
    onDone,
}) {
    const { t } = useTranslation();
    const inputRef = useRef(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [showAll, setShowAll] = useState(false);
    const [selectedGroupId, setSelectedGroupId] = useState(null);
    const [matched, setMatched] = useState([]);
    const [unmatched, setUnmatched] = useState([]);
    const [assignments, setAssignments] = useState({});
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState({ done: 0, total: 0 });
    const [summary, setSummary] = useState(null);
    const [preview, setPreview] = useState(null); // { src, unmatchedIndex? }

    const hasMultiple = (companies || []).length > 1;
    const singleCompany = (companies || []).length === 1 ? companies[0] : null;

    React.useEffect(() => {
        if (!open) return;
        setSearchQuery('');
        setShowAll(false);
        setMatched([]);
        setUnmatched([]);
        setAssignments({});
        setSummary(null);
        setProgress({ done: 0, total: 0 });
        setPreview(null);
        if (singleCompany) {
            setSelectedGroupId(singleCompany.id);
        } else {
            setSelectedGroupId(null);
        }
    }, [open, singleCompany]);

    const memberKennitalas = useMemo(() => {
        const map = new Map();
        (members || []).forEach((m) => {
            const kt = resolveKennitala(m);
            if (!kt) return;
            const groups = String(m.torgid_special_companies || '')
                .split(',')
                .map((id) => Number(id));
            if (selectedGroupId && !groups.includes(selectedGroupId)) return;
            map.set(kt, m);
        });
        return map;
    }, [members, selectedGroupId]);

    const membersWithoutPhoto = useMemo(() => {
        const list = [];
        (members || []).forEach((m) => {
            const kt = resolveKennitala(m);
            if (!kt) return;
            const groups = String(m.torgid_special_companies || '')
                .split(',')
                .map((id) => Number(id));
            if (selectedGroupId && !groups.includes(selectedGroupId)) return;
            const hasPhoto =
                m.membership_photos?.[selectedGroupId] ||
                m.membership_photos?.[String(selectedGroupId)];
            if (!hasPhoto) {
                list.push({
                    kennitala: kt,
                    name: m.torgid_user?.name || m.name || kt,
                });
            }
        });
        return list;
    }, [members, selectedGroupId]);

    const visibleCompanies = useMemo(() => {
        const all = companies || [];
        const filtered = searchQuery.trim()
            ? all.filter((c) => c.name.toLowerCase().includes(searchQuery.toLowerCase()))
            : all;
        if (searchQuery.trim() || showAll) return filtered;
        return filtered.slice(0, 15);
    }, [companies, searchQuery, showAll]);

    const processFiles = async (fileList) => {
        const files = Array.from(fileList || []).filter((f) =>
            /\.(jpe?g|png)$/i.test(f.name)
        );
        const nextMatched = [];
        const nextUnmatched = [];
        const seenKt = new Set();

        for (const file of files) {
            const kt = parseKennitalaFromFilename(file.name);
            const preview = URL.createObjectURL(file);
            if (!kt || !memberKennitalas.has(kt) || seenKt.has(kt)) {
                nextUnmatched.push({ file, preview, reason: !kt ? 'invalid' : 'not_member' });
                continue;
            }
            seenKt.add(kt);
            const member = memberKennitalas.get(kt);
            const hadPhoto =
                !!member.membership_photos?.[selectedGroupId] ||
                !!member.membership_photos?.[String(selectedGroupId)];
            nextMatched.push({ file, preview, kennitala: kt, hadPhoto });
        }

        setMatched(nextMatched);
        setUnmatched(nextUnmatched);
        setAssignments({});
        setSummary(null);
    };

    const handleUpload = async () => {
        if (!selectedGroupId) return;
        const manual = unmatched
            .map((item, index) => {
                const kt = assignments[index];
                if (!kt) return null;
                return { ...item, kennitala: kt };
            })
            .filter(Boolean);

        const queue = [
            ...matched.map((m) => ({ ...m, kind: m.hadPhoto ? 'overwrite' : 'success' })),
            ...manual.map((m) => ({ ...m, kind: 'manual' })),
        ];

        setUploading(true);
        setProgress({ done: 0, total: queue.length });
        const counts = { success: 0, overwritten: 0, manual: 0, skipped: unmatched.length - manual.length };

        try {
            for (let i = 0; i < queue.length; i++) {
                const item = queue[i];
                const dataUrl = await fileToDataUrl(item.file);
                await api.put(specialUserApi.upsertMembershipPhoto(), {
                    group_id: selectedGroupId,
                    kennitala: item.kennitala,
                    image: dataUrl,
                });
                if (item.kind === 'overwrite') counts.overwritten += 1;
                else if (item.kind === 'manual') counts.manual += 1;
                else counts.success += 1;
                setProgress({ done: i + 1, total: queue.length });
            }
            setSummary(counts);
            onDone?.();
        } catch (e) {
            console.error(e);
        } finally {
            setUploading(false);
        }
    };

    const assignedKts = new Set(Object.values(assignments).filter(Boolean));

    return (
        <Dialog
            open={open}
            onClose={uploading ? undefined : onClose}
            maxWidth={false}
            PaperProps={{ style: { width: '700px', maxWidth: '95vw', maxHeight: '95vh' } }}
        >
            <DialogTitle>{t('membership_photo_bulk_title')}</DialogTitle>
            <DialogContent dividers>
                {hasMultiple ? (
                    <>
                        <h4 className={styles.sectionTitle}>{t('membership_photo_select_group')}</h4>
                        <p className={styles.sectionDescription}>
                            {t('membership_photo_select_group_description')}
                        </p>
                        <input
                            type="text"
                            placeholder={t('search_groups', 'Leita að hópum...')}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className={styles.search}
                        />
                        <div className={styles.companiesGrid}>
                            {visibleCompanies.map((comp) => {
                                const isSelected = selectedGroupId === comp.id;
                                return (
                                    <div
                                        key={comp.id}
                                        className={`${styles.companyCheckbox} ${
                                            isSelected ? styles.iconSelected : ''
                                        }`}
                                        onClick={() => setSelectedGroupId(comp.id)}
                                        role="button"
                                        tabIndex={0}
                                    >
                                        <Checkbox checked={isSelected} tabIndex={-1} />
                                        <img src={comp.logo} className={styles.logoImg} alt="" />
                                        <span className={styles.companyName}>{comp.name}</span>
                                    </div>
                                );
                            })}
                        </div>
                        {!searchQuery.trim() && !showAll && (companies || []).length > 15 && (
                            <div style={{ textAlign: 'center', marginTop: 16 }}>
                                <button type="button" className={styles.showMore} onClick={() => setShowAll(true)}>
                                    {t('show_more', 'Sýna fleiri')}
                                </button>
                            </div>
                        )}
                    </>
                ) : (
                    singleCompany && (
                        <div className={styles.singleCompanyInfo}>
                            <img src={singleCompany.logo} className={styles.logoImg} alt="" />
                            <h4 className={styles.sectionTitle}>
                                {t('membership_photo_bulk_single_group')} „{singleCompany.name}“
                            </h4>
                        </div>
                    )
                )}

                {selectedGroupId && !summary && (
                    <>
                        <div
                            className={styles.dropZone}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => {
                                e.preventDefault();
                                processFiles(e.dataTransfer.files);
                            }}
                            onClick={() => inputRef.current?.click()}
                        >
                            <p>{t('membership_photo_drop_hint')}</p>
                            <input
                                ref={inputRef}
                                type="file"
                                accept=".jpg,.jpeg,.png"
                                multiple
                                hidden
                                onChange={(e) => processFiles(e.target.files)}
                            />
                        </div>

                        {matched.length > 0 && (
                            <Typography sx={{ mt: 2, mb: 1 }}>
                                {t('membership_photo_success')}: {matched.length}
                            </Typography>
                        )}
                        <div className={styles.previewGrid}>
                            {matched.map((item) => (
                                <div key={item.kennitala} className={styles.previewCard}>
                                    <button
                                        type="button"
                                        className={styles.thumbButton}
                                        onClick={() => setPreview({ src: item.preview })}
                                        aria-label={t('membership_photo')}
                                    >
                                        <img src={item.preview} alt="" />
                                    </button>
                                    <span>{item.kennitala}</span>
                                </div>
                            ))}
                        </div>

                        {unmatched.length > 0 && (
                            <>
                                <Typography sx={{ mt: 2, mb: 1 }} color="error">
                                    {t('membership_photo_unmatched')} ({unmatched.length})
                                </Typography>
                                {unmatched.map((item, index) => (
                                    <div key={index} className={styles.unmatchedRow}>
                                        <button
                                            type="button"
                                            className={styles.thumbButton}
                                            onClick={() =>
                                                setPreview({
                                                    src: item.preview,
                                                    unmatchedIndex: index,
                                                })
                                            }
                                            aria-label={t('membership_photo')}
                                        >
                                            <img
                                                src={item.preview}
                                                alt=""
                                                className={styles.unmatchedImg}
                                            />
                                        </button>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>{t('membership_photo_assign_member')}</InputLabel>
                                            <Select
                                                label={t('membership_photo_assign_member')}
                                                value={assignments[index] || ''}
                                                onChange={(e) =>
                                                    setAssignments((prev) => ({
                                                        ...prev,
                                                        [index]: e.target.value,
                                                    }))
                                                }
                                            >
                                                <MenuItem value="">
                                                    <em>{t('membership_photo_skipped')}</em>
                                                </MenuItem>
                                                {membersWithoutPhoto.map((m) => (
                                                    <MenuItem
                                                        key={m.kennitala}
                                                        value={m.kennitala}
                                                        disabled={
                                                            assignedKts.has(m.kennitala) &&
                                                            assignments[index] !== m.kennitala
                                                        }
                                                    >
                                                        {m.name} ({m.kennitala})
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </div>
                                ))}
                            </>
                        )}
                    </>
                )}

                {uploading && (
                    <LinearProgress
                        variant="determinate"
                        value={progress.total ? (progress.done / progress.total) * 100 : 0}
                        sx={{ mt: 2 }}
                    />
                )}

                {summary && (
                    <div className={styles.summary}>
                        <Typography variant="h6">{t('membership_photo_summary')}</Typography>
                        <p>
                            {t('membership_photo_success')}: {summary.success}
                        </p>
                        <p>
                            {t('membership_photo_overwritten')}: {summary.overwritten}
                        </p>
                        <p>
                            {t('membership_photo_manual')}: {summary.manual}
                        </p>
                        <p>
                            {t('membership_photo_skipped')}: {summary.skipped}
                        </p>
                    </div>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={uploading}>
                    {summary ? t('ok', { defaultValue: 'Loka' }) : t('cancel')}
                </Button>
                {!summary && (
                    <Button
                        color="primary"
                        variant="contained"
                        disabled={
                            uploading ||
                            !selectedGroupId ||
                            (matched.length === 0 &&
                                !Object.values(assignments).some(Boolean))
                        }
                        onClick={handleUpload}
                    >
                        {uploading ? <CircularProgress size={20} /> : t('membership_photo_upload')}
                    </Button>
                )}
            </DialogActions>
            <Dialog
                open={!!preview}
                onClose={() => setPreview(null)}
                maxWidth="sm"
                fullWidth
            >
                <DialogContent className={styles.lightboxContent}>
                    {preview?.src ? (
                        <img src={preview.src} alt="" className={styles.lightboxImg} />
                    ) : null}
                    {preview?.unmatchedIndex != null && (
                        <FormControl fullWidth size="small" className={styles.lightboxSelect}>
                            <InputLabel>{t('membership_photo_assign_member')}</InputLabel>
                            <Select
                                label={t('membership_photo_assign_member')}
                                value={assignments[preview.unmatchedIndex] || ''}
                                onChange={(e) =>
                                    setAssignments((prev) => ({
                                        ...prev,
                                        [preview.unmatchedIndex]: e.target.value,
                                    }))
                                }
                                MenuProps={{
                                    // Keep the menu above the nested dialog stacking context.
                                    style: { zIndex: 1500 },
                                }}
                            >
                                <MenuItem value="">
                                    <em>{t('membership_photo_skipped')}</em>
                                </MenuItem>
                                {membersWithoutPhoto.map((m) => (
                                    <MenuItem
                                        key={m.kennitala}
                                        value={m.kennitala}
                                        disabled={
                                            assignedKts.has(m.kennitala) &&
                                            assignments[preview.unmatchedIndex] !== m.kennitala
                                        }
                                    >
                                        {m.name} ({m.kennitala})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPreview(null)}>
                        {t('close', { defaultValue: 'Loka' })}
                    </Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
}
